/**
 * Placed item on an explicit CSS grid (editor / collision model).
 * `col` is 0-based; `row` is 0-based; spans are >= 1.
 */
export type GridLayoutPlacedItem = {
  id: string
  col: number
  row: number
  colSpan: number
  /** @default 1 */
  rowSpan?: number
  /** Minimum columns when resizing. @default 1 */
  minColSpan?: number
  /**
   * Widget-owned CSS min width (e.g. `"280px"`). Editor converts this to a
   * column floor from track width when resizing.
   */
  minWidth?: string
}

export const GridLayoutCollision = {
  /**
   * Place items in stable reading order (row, then col). Does not search for
   * holes — if the next item does not fit on the current row, wrap and push
   * following content down. When `fillSoloRows` is true, a lone item on a row
   * expands to the full track count.
   */
  layoutInOrder: (
    items: GridLayoutPlacedItem[],
    columns: number,
    options?: { fillSoloRows?: boolean },
  ): GridLayoutPlacedItem[] => {
    const ordered = [...items].sort((a, b) => a.row - b.row || a.col - b.col)
    const packed = GridLayoutCollision.pack(ordered, columns)
    return options?.fillSoloRows
      ? GridLayoutCollision.fillRemainingTracks(packed, columns)
      : packed
  },

  /**
   * After a resize commit: re-lay out in reading order so an expanded item
   * pushes followers down (no hole-hunting / reordering). Never solo-fills —
   * that is display-only via {@link fillRemainingTracks}.
   */
  resolveAfterResize: (
    items: GridLayoutPlacedItem[],
    _resizedId: string,
    columns: number,
  ): GridLayoutPlacedItem[] =>
    GridLayoutCollision.layoutInOrder(items, columns),

  /**
   * Parse a CSS px length (e.g. `"24px"`). Returns undefined if unset or not
   * parseable.
   */
  parsePxLength: (value?: string): number | undefined => {
    if (value == null || value === '') return undefined
    const match = /^(\d+(?:\.\d+)?)px$/i.exec(value.trim())
    if (!match) return undefined
    const parsed = Number(match[1])
    return Number.isFinite(parsed) ? parsed : undefined
  },

  /** Parse `minWidth` as px. Alias of {@link parsePxLength}. */
  parseMinWidthPx: (minWidth?: string): number | undefined =>
    GridLayoutCollision.parsePxLength(minWidth),

  /**
   * Absolute column floor from `minWidth` / `minColSpan` (1…columns), ignoring
   * the item's current column (caller adjusts col when it can't fit).
   */
  requiredColSpan: (
    item: Pick<GridLayoutPlacedItem, 'minColSpan' | 'minWidth'>,
    columns: number,
    trackWidthPx: number,
    gapPx: number,
  ): number => {
    const declared = Math.max(1, item.minColSpan ?? 1)
    const minWidthPx = GridLayoutCollision.parseMinWidthPx(item.minWidth)
    if (minWidthPx == null || trackWidthPx <= 0) {
      return Math.min(declared, columns)
    }
    const fromWidth = Math.ceil((minWidthPx + gapPx) / (trackWidthPx + gapPx))
    return Math.min(columns, Math.max(declared, fromWidth, 1))
  },

  /**
   * Column floor at the item's current `col` (for resize clamping).
   */
  minColSpanForWidth: (
    item: Pick<GridLayoutPlacedItem, 'col' | 'minColSpan' | 'minWidth'>,
    columns: number,
    trackWidthPx: number,
    gapPx: number,
  ): number => {
    const required = GridLayoutCollision.requiredColSpan(
      item,
      columns,
      trackWidthPx,
      gapPx,
    )
    return Math.min(required, Math.max(1, columns - item.col))
  },

  /**
   * Grow a solo item on a row so it consumes the full track count (display
   * only). Not for authored pack / resize resolve.
   *
   * @param onlyPushed When true, only fill a solo widget that was pushed to a
   *   new row because the previous item in reading order could not share the
   *   row (`prev.colSpan + solo.colSpan > columns`). Leaves intentional sizes
   *   like 3/4 alone unfilled. When false, every solo row fills (viewport
   *   min-width reflow).
   */
  fillRemainingTracks: (
    items: GridLayoutPlacedItem[],
    columns: number,
    options?: { onlyPushed?: boolean },
  ): GridLayoutPlacedItem[] => {
    if (items.length === 0 || columns < 1) return items

    const onlyPushed = options?.onlyPushed ?? false
    const ordered = [...items].sort((a, b) => a.row - b.row || a.col - b.col)
    const indexById = new Map(ordered.map((item, index) => [item.id, index]))

    const rowIds = new Map<number, string[]>()
    for (const item of items) {
      const ids = rowIds.get(item.row) ?? []
      ids.push(item.id)
      rowIds.set(item.row, ids)
    }

    const byId = new Map(items.map(item => [item.id, { ...item }]))

    for (const ids of rowIds.values()) {
      const rowItems = ids
        .map(id => byId.get(id)!)
        .sort((a, b) => a.col - b.col)
      if (rowItems.length !== 1) continue

      const solo = rowItems[0]
      if (onlyPushed) {
        const index = indexById.get(solo.id) ?? -1
        const prev = index > 0 ? ordered[index - 1] : null
        const wasPushed =
          prev != null &&
          prev.row < solo.row &&
          prev.colSpan + solo.colSpan > columns
        if (!wasPushed) continue
      }

      byId.set(solo.id, { ...solo, col: 0, colSpan: columns })
    }

    return items.map(item => byId.get(item.id) ?? item)
  },

  /**
   * Responsive display layout from an authored layout.
   * Grows spans when the track cannot honor `minWidth`, lays out in reading
   * order (push followers down — never hole-hunt), then fills leftover tracks
   * on each display row. Always derive from authored items — never write back —
   * so a wider viewport restores the original col/row/colSpan.
   */
  reflowForViewport: (
    items: GridLayoutPlacedItem[],
    columns: number,
    trackWidthPx: number,
    gapPx: number,
  ): GridLayoutPlacedItem[] => {
    if (trackWidthPx <= 0 || items.length === 0) return items

    const needsReflow = items.some(item => {
      const required = GridLayoutCollision.requiredColSpan(
        item,
        columns,
        trackWidthPx,
        gapPx,
      )
      return item.colSpan < required || item.col + required > columns
    })

    if (!needsReflow) return items

    const ordered = [...items].sort((a, b) => a.row - b.row || a.col - b.col)
    const withSpans = ordered.map(item => {
      const required = GridLayoutCollision.requiredColSpan(
        item,
        columns,
        trackWidthPx,
        gapPx,
      )
      return {
        ...item,
        colSpan: Math.max(item.colSpan, required),
      }
    })

    const packed = GridLayoutCollision.layoutInOrder(withSpans, columns, {
      fillSoloRows: true,
    })
    return packed
  },

  /**
   * Clamp colSpan so the item stays on the grid and respects minColSpan.
   * `threshold` is how far into the next column (0–1) before snapping up.
   * Default 0.5 matches Math.round; lower values snap earlier while expanding.
   * Pass `minColSpanOverride` when width-derived floor exceeds declared min.
   */
  clampColSpan: (
    item: Pick<GridLayoutPlacedItem, 'col' | 'colSpan' | 'minColSpan'>,
    columns: number,
    nextColSpan: number,
    threshold = 0.5,
    minColSpanOverride?: number,
  ): number => {
    const min = Math.max(1, minColSpanOverride ?? item.minColSpan ?? 1)
    const max = Math.max(min, columns - item.col)
    const snapped = Math.floor(nextColSpan + (1 - threshold))
    return Math.min(Math.max(snapped, min), max)
  },

  /**
   * Equal-share column span when every peer omits an explicit `colSpan`.
   * Same width for each item; leftover tracks stay empty on the row.
   */
  equalShareColSpan: (itemCount: number, columns: number): number => {
    if (itemCount <= 0) return Math.max(1, columns)
    return Math.max(1, Math.floor(columns / itemCount))
  },

  /**
   * Pack items left-to-right, top-to-bottom using each item's colSpan
   * (ignores prior col/row). Useful to seed a layout from an ordered list.
   */
  pack: (
    items: Array<
      Omit<GridLayoutPlacedItem, 'col' | 'row'> &
        Partial<Pick<GridLayoutPlacedItem, 'col' | 'row'>>
    >,
    columns: number,
  ): GridLayoutPlacedItem[] => {
    const placed: GridLayoutPlacedItem[] = []
    let col = 0
    let row = 0
    let rowHeight = 1

    for (const item of items) {
      const span = Math.min(Math.max(item.colSpan, 1), columns)
      const rowSpan = item.rowSpan ?? 1
      if (col + span > columns) {
        col = 0
        row += rowHeight
        rowHeight = 1
      }
      placed.push({
        id: item.id,
        colSpan: span,
        rowSpan,
        minColSpan: item.minColSpan,
        minWidth: item.minWidth,
        col,
        row,
      })
      col += span
      rowHeight = Math.max(rowHeight, rowSpan)
    }

    return placed
  },
}
